/**
 * What a 401 or 403 from one of Studio's APIs means for the session (BT-2060).
 *
 * The response interceptor used to treat every 401 and 403 as an expired Studio
 * session and hard-navigate to sign-in. But an API can refuse a request while
 * the session is perfectly alive: Staging's client-search gateway refused the
 * New crosstab dialog's "Filter by client" lookup (BT-2060), subsidiary-access
 * 403s for a standalone org (BT-2011), and /users/log-route 401ed when it fired
 * before the token cache was filled (see `src/providers/enhanced-session.tsx`).
 * Sign-in then saw a live session and sent the tab straight back (or Home), so
 * the person lost what they were doing and never learned why.
 *
 * The rule: ask the auth server. Only a session it says it no longer has goes
 * to sign-in. A 401 sent with a token the live session has since replaced (or
 * with none) is retried once with the current token. Anything else is the API's
 * own refusal, and the call simply fails so its caller can show an error state.
 */

import type { Session } from 'next-auth';

import { Routes } from '@/app/routes';

/** What GET /api/auth/session said when asked. */
export type SessionProbe =
    | { state: 'live'; session: Session }
    | { state: 'gone' }
    | { state: 'unknown' };

export type AuthRejectionAction = 'sign-in' | 'retry' | 'reload' | 'reject';

/** The probe can run a Cognito refresh and four profile calls; do not wait longer. */
export const SESSION_PROBE_TIMEOUT_MS = 10_000;

/** How long a token the auth server just confirmed is trusted without asking again. */
export const LIVE_TOKEN_TRUST_MS = 30_000;

export function isAuthRejection(status: number | undefined): status is 401 | 403 {
    return status === 401 || status === 403;
}

/**
 * Pure: read the session endpoint's answer. NextAuth answers 200 `{}` when it
 * has no session, including after clearing one whose session callback threw.
 * Anything it did not clearly say — an error status, a body that is not JSON,
 * a user carrying an error — is unknown, never "gone": sending a live session
 * to sign-in is exactly the bounce this module exists to stop.
 */
export function readSessionAnswer(ok: boolean, body: unknown): SessionProbe {
    if (!ok || !body || typeof body !== 'object') return { state: 'unknown' };
    if (Object.keys(body).length === 0) return { state: 'gone' };
    const session = body as Session;
    if (session.user && !session.error) return { state: 'live', session };
    return { state: 'unknown' };
}

/**
 * Asks the auth server for the session. Uses fetch rather than next-auth's
 * `getSession`, which returns null for "no session" and "could not ask" alike.
 * GET /api/auth/session runs the jwt callback, so a live answer carries the
 * freshest tokens.
 */
export async function probeSession(timeoutMs = SESSION_PROBE_TIMEOUT_MS): Promise<SessionProbe> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch('/api/auth/session', {
            cache: 'no-store',
            signal: controller.signal,
        });
        return readSessionAnswer(res.ok, await res.json());
    } catch {
        return { state: 'unknown' };
    } finally {
        clearTimeout(timer);
    }
}

let probeInFlight: Promise<SessionProbe> | null = null;

/** One probe for any number of refusals that arrive together. */
export function sharedSessionProbe(): Promise<SessionProbe> {
    if (!probeInFlight) {
        probeInFlight = probeSession().finally(() => {
            probeInFlight = null;
        });
    }
    return probeInFlight;
}

let trustedAuthorization: { value: string; until: number } | null = null;

/** Remember that the auth server just confirmed this Authorization header. */
export function trustAuthorization(value: string | undefined): void {
    trustedAuthorization = value ? { value, until: Date.now() + LIVE_TOKEN_TRUST_MS } : null;
}

/** Whether a refusal carrying this header can skip asking the auth server again. */
export function isTrustedAuthorization(value: string | undefined): boolean {
    if (!value || !trustedAuthorization) return false;
    return trustedAuthorization.value === value && Date.now() < trustedAuthorization.until;
}

/** The Authorization header a live session's requests carry. */
export function authorizationFor(session: Session): string | undefined {
    return session.idToken ? `Bearer ${session.idToken}` : undefined;
}

/**
 * Pure: what to do with a request an API refused.
 *
 * - The auth server has no session: sign in.
 * - It could not say: fail the call. Guessing "gone" would bounce a live session.
 * - It has a session for someone else (another tab signed in as a different
 *   user): reload, so the page is rebuilt for them. Never re-send as them.
 * - A 401 sent with a token the live session has since replaced, or with none:
 *   retry once with the current token. A 403 is a deliberate refusal, not a
 *   stale token, so it is never retried.
 * - Otherwise the API refused a live session: fail the call.
 */
export function resolveAuthRejection({
    probe,
    status,
    sentAuthorization,
    sentAsUserId,
    alreadyRetried,
}: {
    probe: SessionProbe;
    status: 401 | 403;
    sentAuthorization: string | undefined;
    sentAsUserId: string | undefined;
    alreadyRetried: boolean;
}): AuthRejectionAction {
    if (probe.state === 'gone') return 'sign-in';
    if (probe.state === 'unknown') return 'reject';
    const liveUserId = probe.session.user?.id;
    if (sentAsUserId && liveUserId && liveUserId !== sentAsUserId) return 'reload';
    const current = authorizationFor(probe.session);
    if (status === 401 && !alreadyRetried && current && current !== sentAuthorization) {
        return 'retry';
    }
    return 'reject';
}

/**
 * The sign-in URL for the page the person is on, or null when they are already
 * on sign-in. Relative, so it stays on whichever domain is serving the app.
 */
export function signInUrlFor(location: Pick<Location, 'pathname' | 'search'>): string | null {
    if (location.pathname.includes(Routes.SIGNIN)) return null;
    const callbackUrl = encodeURIComponent(location.pathname + location.search);
    return `${Routes.SIGNIN}?error=SessionExpired&callbackUrl=${callbackUrl}`;
}

/** Hard navigation, in one place so tests can stand in for it. */
export function navigateTo(url: string): void {
    window.location.href = url;
}

/** Full reload, in one place so tests can stand in for it. */
export function reloadPage(): void {
    window.location.reload();
}

/** Clears the in-flight probe and the trusted header between tests. */
export function __resetAuthRejectionForTests(): void {
    probeInFlight = null;
    trustedAuthorization = null;
}
